import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";

import { uniq } from "lodash";
import moment from "moment";
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import NoResultsGif from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import LockedContent from "../../../../grace-period/components/LockedContent";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import SwabListModal from "./SwabListModal";
import TestVolumeTrendTooltip, { TrendChartCursor } from "./TestVolumeTrendTooltip";
import TrendChartPill from "./TrendChartPill";

import { AnalyticsColors } from "../../../../../styles/tokens";

import pumpkinGif from "../../../../../assets/images/environment/pumpkin.gif";

import "./TestVolumeTrendChart.css";

const DEFAULT_VISIBLE_TESTS = 5;
const DEFAULT_VISIBLE_DATES = 10;
const HOVER_STROKE = "var(--color-primary)";
const DOT_RADIUS = 4;
const ACTIVE_DOT_RADIUS = 5;

const VolumeTrendDot = ({
  cx,
  cy,
  stroke,
  payload,
  dataKey,
  value,
  onDotClick,
  radius = DOT_RADIUS,
}) => {
  if (value == null || cx == null || cy == null) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill="var(--color-bg-primary)"
      stroke={stroke}
      strokeWidth={2}
      className="TestVolumeTrendChart__Dot"
      onClick={(event) => {
        event.stopPropagation();
        onDotClick(payload.date, dataKey);
      }}
    />
  );
};

const groupDataByDate = (data) => {
  const dateMap = {};

  data.forEach((test) => {
    const testName = test.test_name;
    const { details } = test;

    Object.keys(details).forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = { date: moment(date).format("YYYY-MM-DD") };
      }
      dateMap[date][testName] = details[date];
    });
  });

  return Object.values(dateMap).sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );
};

const formatXAxisTick = (tickItem) => {
  const date = moment(tickItem);
  if (!date.isValid()) return "";
  return date.format("MMM D");
};

const TestVolumeTrendChart = ({ graphData = [] }) => {
  const { areResultsLocked } = useAccessStatus();
  const [selectedTests, setSelectedTests] = useState([]);
  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: DEFAULT_VISIBLE_DATES - 1 });
  const [activeLabel, setActiveLabel] = useState(null);
  const [swabListModal, setSwabListModal] = useState({
    open: false,
    date: null,
  });

  const allTestNames = useMemo(() => {
    if (!graphData?.length) return [];
    return uniq(graphData.map((item) => item.test_name));
  }, [graphData]);

  const testColorMap = useMemo(() => {
    const map = {};
    allTestNames.forEach((testName, index) => {
      map[testName] = AnalyticsColors[index % AnalyticsColors.length];
    });
    return map;
  }, [allTestNames]);

  const chartData = useMemo(() => {
    if (!graphData?.length) return [];
    return groupDataByDate(graphData);
  }, [graphData]);

  const testNamesKey = useMemo(() => allTestNames.join("|"), [allTestNames]);

  useEffect(() => {
    setSelectedTests(allTestNames.slice(0, DEFAULT_VISIBLE_TESTS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testNamesKey]);

  const visibleSelectedTests = useMemo(
    () => selectedTests.filter((testName) => testColorMap[testName]),
    [selectedTests, testColorMap],
  );

  const handleTestToggle = useCallback((testName) => {
    setSelectedTests((prev) => (
      prev.includes(testName)
        ? prev.filter((name) => name !== testName)
        : [...prev, testName]
    ));
  }, []);

  const handleViewSwabList = useCallback((date) => {
    setSwabListModal({
      open: true,
      date,
    });
  }, []);

  const handleCloseSwabList = useCallback(() => {
    setSwabListModal({ open: false, date: null });
  }, []);

  const renderXAxisTick = useCallback(({ x, y, payload }) => {
    const isActive = activeLabel === payload.value;
    return (
      <text
        x={x}
        y={y + 12}
        textAnchor="middle"
        fill={isActive ? HOVER_STROKE : "var(--color-content-secondary)"}
        fontSize={11}
        fontFamily="Roboto"
      >
        {formatXAxisTick(payload.value)}
      </text>
    );
  }, [activeLabel]);

  const filteredChartData = useMemo(() => (
    chartData.map((entry) => {
      const filtered = { date: entry.date };
      visibleSelectedTests.forEach((test) => {
        if (entry[test] != null) {
          filtered[test] = entry[test];
        }
      });
      return filtered;
    })
  ), [chartData, visibleSelectedTests]);

  const showBrush = filteredChartData.length > DEFAULT_VISIBLE_DATES;

  useEffect(() => {
    const lastIndex = Math.max(0, filteredChartData.length - 1);
    const endIndex = Math.min(DEFAULT_VISIBLE_DATES - 1, lastIndex);
    setBrushRange({ startIndex: 0, endIndex });
    // Reset the brush window only when the number of data points changes
    // (a genuinely new dataset). Depending on the array reference instead makes
    // the effect re-run on unrelated re-renders and snaps the slider back to the
    // start mid-drag, so it can't be moved freely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredChartData.length]);

  const visibleChartData = useMemo(() => {
    if (!showBrush) return filteredChartData;

    const { startIndex, endIndex } = brushRange;
    return filteredChartData.slice(startIndex, endIndex + 1);
  }, [filteredChartData, brushRange, showBrush]);

  const handleBrushChange = useCallback((range) => {
    if (range?.startIndex == null || range?.endIndex == null) return;
    setBrushRange({ startIndex: range.startIndex, endIndex: range.endIndex });
  }, []);

  const formatBrushDate = (date) => {
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format("DD MMM") : "";
  };

  return (
    <div className="TestVolumeTrendChart">
      <div className="TestVolumeTrendChart__Header">
        Test Volume Trend over Time
      </div>
      {allTestNames.length > 0 && (
        <StyledScrollbar
          noScrollY
          autoHide="x"
          className="TestVolumeTrendChart__PillsScrollbar"
        >
          <div className="TestVolumeTrendChart__Pills">
            {allTestNames.map((testName) => (
              <TrendChartPill
                key={testName}
                testName={testName}
                color={testColorMap[testName].color}
                backgroundColor={testColorMap[testName].backgroundColor}
                isSelected={selectedTests.includes(testName)}
                onClick={handleTestToggle}
              />
            ))}
          </div>
        </StyledScrollbar>
      )}
      <div className={`TestVolumeTrendChart__Body${areResultsLocked ? " TestVolumeTrendChart__Body--locked" : ""}`}>
        {areResultsLocked ? (
          <LockedContent variant="line" />
        ) : graphData?.length > 0 && visibleSelectedTests.length > 0 ? (
          <div className="TestVolumeTrendChart__ChartWrapper">
            <div className="TestVolumeTrendChart__ChartArea">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={visibleChartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                  onMouseMove={(state) => {
                    if (state?.activeLabel) {
                      setActiveLabel(state.activeLabel);
                    }
                  }}
                  onMouseLeave={() => setActiveLabel(null)}
                >
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke="var(--color-border-secondary)"
                    vertical
                    horizontal
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tick={renderXAxisTick}
                    interval="preserveStartEnd"
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    tick={{
                      fill: "var(--color-content-secondary)",
                      fontSize: 11,
                      fontFamily: "Roboto",
                    }}
                    domain={[0, "auto"]}
                    width={32}
                  />
                  <Tooltip
                    wrapperStyle={{ pointerEvents: "auto" }}
                    content={(props) => (
                      <TestVolumeTrendTooltip
                        {...props}
                        testColors={testColorMap}
                        onViewDetails={handleViewSwabList}
                      />
                    )}
                    cursor={<TrendChartCursor />}
                  />
                  {visibleSelectedTests.map((testName) => (
                    <Line
                      key={testName}
                      type="monotone"
                      dataKey={testName}
                      name={testName}
                      stroke={testColorMap[testName].color}
                      strokeWidth={2}
                      connectNulls
                      dot={(props) => (
                        <VolumeTrendDot
                          {...props}
                          stroke={testColorMap[testName].color}
                          onDotClick={handleViewSwabList}
                        />
                      )}
                      activeDot={(props) => (
                        <VolumeTrendDot
                          {...props}
                          stroke={HOVER_STROKE}
                          radius={ACTIVE_DOT_RADIUS}
                          onDotClick={handleViewSwabList}
                        />
                      )}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            {showBrush && (
              <div className="TestVolumeTrendChart__BrushArea">
                <ResponsiveContainer width="100%" height={52}>
                  <LineChart
                    data={filteredChartData}
                    margin={{
                      top: 2,
                      right: 20,
                      left: 0,
                      bottom: 2,
                    }}
                  >
                    <XAxis
                      dataKey="date"
                      hide
                      padding={{ left: 10, right: 10 }}
                    />
                    <YAxis
                      width={32}
                      tick={false}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Brush
                      dataKey="date"
                      startIndex={brushRange.startIndex}
                      endIndex={brushRange.endIndex}
                      onChange={handleBrushChange}
                      height={36}
                      stroke="var(--color-border-secondary)"
                      fill="var(--color-cool-surface)"
                      travellerWidth={10}
                      tickFormatter={(value) => formatBrushDate(value)}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ) : graphData?.length > 0 ? (
          <div className="TestVolumeTrendChart__Placeholder" role="alert">
            Please select at least one test to display the graph.
          </div>
        ) : (
          <NoResultsGif message="No Data" image={pumpkinGif} className="TestVolumeTrendChart__NoResults" />
        )}
      </div>
      {swabListModal.open && (
        <SwabListModal
          open={swabListModal.open}
          onClose={handleCloseSwabList}
          date={swabListModal.date}
        />
      )}
    </div>
  );
};

export default TestVolumeTrendChart;
